import React, { useEffect, useCallback, useState } from 'react';
import { Select, Row, Table, Tooltip, Button } from 'antd';
import Navbar from 'src/common/Navbar/Navbar';
import {
  AssignedVehiclesProps,
  AssignedVehiclesRecord,
} from 'src/components/pages/AssignedVehicles/AssignedVehicles.utils';
import { ReloadOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import {
  getAssignedVehicles,
} from 'src/components/pages/AssignedVehicles/AssignedVehicles.actionsCreator';
import { formatDateTime } from 'src/utils/utils';
import { AssignedVehicleStyles } from 'src/components/pages/AssignedVehicles/AssignedVehicles.styles';
import SimplePaginator from 'src/common/SimplePaginator';
import VendorCodeFilter from 'src/components/vendorcodefilter/VendorCodeFilter';
import {
  SET_ASSIGNED_VEHICLES_PAGINATION,
} from 'src/common/actions/constants';
import AddNewVehicleModal from './AddNewVehicleModal';
import JsonArrayTableViewModal from 'src/common/JsonArrayTableViewModal';
import { renderCustomFieldValue } from 'src/common/renderCustomFieldValue';

const AssignedVehicles: React.FC<AssignedVehiclesProps> = (props: AssignedVehiclesProps) => {
  const {
    classes,
    assignedVehicles,
    getAssignedVehicles,
    setPagination,
    columnList,
    customFieldsList,
    allowVehicleUpdate,
    t,
  } = props;

  const { pagination } = assignedVehicles;
  const [state, setState] = useState<{
    isVehicleModalVisible: boolean;
    action?: 'create' | 'edit';
    selectedVehicle: AssignedVehiclesRecord | null;
    selectedVendors: any[];
  }>({
    isVehicleModalVisible: false,
    action: undefined,
    selectedVehicle: null,
    selectedVendors: [],
  });

  const [filters, setFilters] = useState({
    vendorIdsToFilter: [] as string[],
  });

  const [jsonModalState, setJsonModalState] = useState<{
    isVisible: boolean;
    data: any[];
    fields: any[];
  }>({
    isVisible: false,
    data: [],
    fields: [],
  });
  const fetchViewDetailsAndCount = () => {
    getAssignedVehicles({ ...pagination, ...filters });
  };

  useEffect(() => {
    fetchViewDetailsAndCount();
  }, [pagination, filters]);

  const handleCloseModal = (modalName: string) => {
    setState((prevState) => ({
      ...prevState,
      [modalName]: false,
      action: undefined,
      selectedVehicle: null,
    }));
  };

  const showModals = useCallback((modalName: string, record?: AssignedVehiclesRecord,
    action?: string) => {
    setState((prevState) => ({
      ...prevState,
      [modalName]: true,
      action: action as 'create' | 'edit' | undefined,
      selectedVehicle: record || null,
    }));
  }, []);

  const handleVendorFilterChange = (value: any) => {
    const vendors = Array.isArray(value) ? value : [];
    setState((prevState) => ({
      ...prevState,
      selectedVendors: vendors,
    }));
    const vendorIds = vendors.map((item) => item.value);
    setFilters({ vendorIdsToFilter: vendorIds });
  };

  const generateColumnsToShow = useCallback(() => {
    const columnData: {
      title: string;
      sorter: boolean;
      dataIndex: string;
      key: string;
      width: number;
      fixed?: 'left' | 'right';
      render?: (value: any, record: AssignedVehiclesRecord) => JSX.Element | string;
    }[] = [];

    columnList?.forEach((item) => {
      const column_id = item.is_sortable ? item.sort_key : item.column_id;
      const itemToPush = {
        title: t(item.pretty_name),
        sorter: item.is_sortable,
        dataIndex: column_id,
        key: column_id,
        width: 190,
      };
      if (['vehicle_vendor_code', 'vendor_code', 'vehicle_code'].includes(item.column_id)) {
        itemToPush['fixed'] = 'left';
      }
      if (column_id === 'actions' && !allowVehicleUpdate) {
          return;
      }

      if (column_id === 'created_at') {
        itemToPush['render'] = (x) => {
          if (x) return formatDateTime(x);
          return 'N/A';
        };
      }
      if (column_id === 'weight_capacity') {
        itemToPush['render'] = (_, record) => _;
      }
      if (column_id === 'vehicle_code') {
        itemToPush['render'] = (_, record) => _;
      }
      if (column_id === 'vehicle_make_code') {
        itemToPush['render'] = (_, record) => _;
      }
      if (column_id === 'vehicle_number') {
        itemToPush['render'] = (_, record) => _;
      }
      if (column_id === 'is_active') {
        itemToPush['render'] = (_, record) => (
          record.is_active ? <CheckOutlined /> : <CloseOutlined />
        );
      }
      if (column_id === 'fleet_type') {
        itemToPush['render'] = (_, record) => (
          t(record.fleet_type)
        );
      }
      if (column_id === 'actions') {
        itemToPush['fixed'] = 'right';
        itemToPush['width'] = 100;
        itemToPush['render'] = (_, record) => {
          return allowVehicleUpdate ? (
            <Button
              onClick={() => showModals('isVehicleModalVisible', record, 'edit')}
              disabled={!record.is_active}
            >
              {t('Edit')}
            </Button>
          ) : null;
        };
      }
      columnData.push(itemToPush);
    });

    // Append custom-field columns before the fixed 'actions' column
    const customColumns = (customFieldsList || []).map((field: any) => ({
      title: t(field.label || field.key),
      sorter: false,
      dataIndex: field.key,
      key: field.key,
      width: 190,
      render: (value: any) => renderCustomFieldValue(value, field, t, (data, fields) => (
        setJsonModalState({ isVisible: true, data, fields })
      )),
    }));
    if (customColumns.length) {
      const actionsIndex = columnData.findIndex((col) => col.key === 'actions');
      if (actionsIndex === -1) {
        columnData.push(...customColumns);
      } else {
        columnData.splice(actionsIndex, 0, ...customColumns);
      }
    }

    return columnData;
  }, [columnList, customFieldsList, allowVehicleUpdate, t, showModals]);

  const renderFilters = () => {
    return (
      <Row style={{ background: '#fff', padding: '12px 16px', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <VendorCodeFilter
            value={state.selectedVendors}
            onChange={handleVendorFilterChange}
            placeholder={t('Vendor Code')}
            mode="multiple"
            style={{ minWidth: 200 }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 1 }}>
          <Button onClick={() => showModals('isVehicleModalVisible', null, 'create')}>
            {t('Add New Vehicle')}
          </Button>
          <SimplePaginator
            loading={assignedVehicles.loader}
            onFirstClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber,
                nextOrPrev: 'first',
              });
            }}
            onPrevClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber - 1,
                nextOrPrev: 'prev',
              });
            }}
            onNextClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber + 1,
                nextOrPrev: 'next',
              });
            }}
            isNext={assignedVehicles.is_next_present}
            pageNumber={pagination.pageNumber}
          />
          <Select
            className={classes.selectWithoutBorder}
            onChange={(resultPerPage) => {
              setPagination({ ...pagination, resultPerPage });
            }}
            value={pagination.resultPerPage}
            style={{ marginLeft: 16 }}
          >
            <Select.Option value={100}>{t('100/page')}</Select.Option>
            <Select.Option value={50}>{t('50/page')}</Select.Option>
            <Select.Option value={20}>{t('20/page')}</Select.Option>
            <Select.Option value={10}>{t('10/page')}</Select.Option>
          </Select>
          <Tooltip title={t("Refresh")}>
            <Button
              onClick={fetchViewDetailsAndCount}
              className={classes.refreshButton}
              style={{ marginLeft: 16 }}
            >
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      </Row>
    );
  };

  const renderViewTable = () => (
    <Table
      rowKey={(record: AssignedVehiclesRecord) => record['id']}
      className={classes.table}
      dataSource={assignedVehicles.page_data || []}
      columns={generateColumnsToShow()}
      loading={assignedVehicles.loader}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );

  const renderNavbar = () => (
    <Navbar
      title={t("Assigned Vehicles")}
    />
  );

  return (
    <div className={classes.mainDiv}>
      {state.isVehicleModalVisible && (
        <AddNewVehicleModal
          isVisible={state.isVehicleModalVisible}
          handleModalClose={() => handleCloseModal('isVehicleModalVisible')}
          action={state.action}
          vehicleData={state.selectedVehicle}
          reloadView={fetchViewDetailsAndCount}
        />
      )}
      {jsonModalState.isVisible && (
        <JsonArrayTableViewModal
          isVisible={jsonModalState.isVisible}
          data={jsonModalState.data}
          fields={jsonModalState.fields}
          handleClose={() => setJsonModalState({ isVisible: false, data: [], fields: [] })}
        />
      )}
      {renderNavbar()}
      <div className={classes.tabsContainer}>
        {renderFilters()}
        {renderViewTable()}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    assigned_vehicle_column_list,
    vehicle_custom_fields,
    vendor_config,
  } = state.masterData;
  return {
    columnList: assigned_vehicle_column_list,
    vendorConfig: vendor_config,
    customFieldsList: vehicle_custom_fields || [],
    assignedVehicles: state.assignedVehicles,
    allowVehicleUpdate: vendor_config?.vehicle_master_config?.allow_vehicle_update || false,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setPagination: (data) =>
      dispatch({
        type: SET_ASSIGNED_VEHICLES_PAGINATION,
        data,
      }),
    getAssignedVehicles: (data) => dispatch(getAssignedVehicles(data)),
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: AssignedVehicleStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(AssignedVehicles);
